import { Route } from 'react-router-dom';
import {
  AppComponents,
  AppRouteBinder,
} from '@backstage/core-app-api';
import { ApiExplorerPage, apiDocsPlugin } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin
} from '@backstage/plugin-catalog';
import { CatalogImportPage, catalogImportPlugin } from '@backstage/plugin-catalog-import';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { orgPlugin } from '@backstage/plugin-org';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import {
  TechDocsIndexPage,
  TechDocsReaderPage,
  techdocsPlugin
} from '@backstage/plugin-techdocs';
import { PlattSignInPage } from '@platt-internal/backstage-plugin-signin-react';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { SearchPage } from '@backstage/plugin-search';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { NotificationsPage } from '@backstage/plugin-notifications';
import {
  MicrosoftSignInFieldExtension
} from '@platt-internal/backstage-plugin-scaffolder-module-azure-field';
import { HomePage, searchPage } from './components';

const defaultScaffolderExtensions = (
  <>
    <MicrosoftSignInFieldExtension />
  </>
);

export const defaultAppComponents: Partial<AppComponents> = {
  SignInPage: props => (
    <PlattSignInPage
      {...props}
      auto
    />
  ),
};

export const bindDefaultRoutes = ({ bind }: { bind: AppRouteBinder } ) => {
  bind(catalogPlugin.externalRoutes, {
    createComponent: scaffolderPlugin.routes.root,
    viewTechDoc: techdocsPlugin.routes.docRoot,
    createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
  });
  bind(apiDocsPlugin.externalRoutes, {
    registerApi: catalogImportPlugin.routes.importPage,
  });
  bind(scaffolderPlugin.externalRoutes, {
    registerComponent: catalogImportPlugin.routes.importPage,
    viewTechDoc: techdocsPlugin.routes.docRoot,
  });
  bind(orgPlugin.externalRoutes, {
    catalogIndex: catalogPlugin.routes.catalogIndex,
  });
};

export type OverrideAppComponents = {
  home?: React.ReactNode;
  techdocsPage?: React.ReactNode;
  techdocsAddons?: React.ReactNode;
};

export type AppRouteProps = {
  entityPage: React.ReactNode;
  scaffolderExtensions?: React.ReactNode;
  overrideComponents?: OverrideAppComponents;
};
export const routes = ({ entityPage, scaffolderExtensions, overrideComponents }: AppRouteProps) => (
  <>
    <Route path="/" element={<HomepageCompositionRoot />}>
      {overrideComponents?.home ?? <HomePage />}
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={overrideComponents?.techdocsPage ?? <TechDocsReaderPage />}
    >
      {overrideComponents?.techdocsAddons ?? (
        <TechDocsAddons>
          <ReportIssue />
        </TechDocsAddons>
      )}
    </Route>
    <Route path="/create" element={<ScaffolderPage />}>
      <ScaffolderFieldExtensions>
        {defaultScaffolderExtensions}
        {scaffolderExtensions}
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
  </>
);
